import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { CardShell, FieldLabel } from "./card-handler";

const CUSTOMER_ATTRIBUTES = [
  "User Response",
  "name",
  "phone",
  "location",
  "Customer attributes",
  "On Template Delivered",
];

interface CustomerAttributeConfig {
  attribute?: string;
  value?: string;
}

interface SetCustomerAttributeCardProps {
  config: CustomerAttributeConfig;
  onUpdate: (config: CustomerAttributeConfig) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const SetCustomerAttributeCard: React.FC<SetCustomerAttributeCardProps> = ({
  config,
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const [searchAttr, setSearchAttr] = useState("");
  const [attrOpen, setAttrOpen] = useState(false);

  const filtered = CUSTOMER_ATTRIBUTES.filter((a) =>
    a.toLowerCase().includes(searchAttr.toLowerCase())
  );

  const update = (patch: Partial<CustomerAttributeConfig>) =>
    onUpdate({ ...config, ...patch });

  return (
    <CardShell title="Set customer attribute" onDelete={onDelete} onReset={onReset} bare={bare}>
        <FieldLabel>Choose attribute</FieldLabel>
        <div className="relative mb-3">
          <button
            onClick={() => setAttrOpen((v) => !v)}
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-indigo-300"
          >
            <span className={config.attribute ? "text-gray-700 dark:text-gray-300" : "text-[#7B7B7B] font-semibold dark:text-gray-500"}>
              {config.attribute || "Select attribute"}
            </span>
            <ChevronDown
              size={16}
              className={`text-[#7B7B7B] font-semibold dark:text-gray-500 transition-transform ${attrOpen ? "rotate-180" : ""}`}
            />
          </button>

          {attrOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="p-2 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1">
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-none stroke-gray-400 stroke-2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    autoFocus
                    className="text-xs outline-none flex-1 text-gray-600 dark:text-gray-300"
                    placeholder="Search"
                    value={searchAttr}
                    onChange={(e) => setSearchAttr(e.target.value)}
                  />
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto">
                {filtered.map((attr) => (
                  <button
                    key={attr}
                    onClick={() => {
                      update({ attribute: attr });
                      setAttrOpen(false);
                      setSearchAttr("");
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors"
                  >
                    {attr}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <FieldLabel>Attribute Value</FieldLabel>
        <div className="relative mb-2">
          <input
            className="w-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 text-sm pr-8 outline-none focus:ring-1 focus:ring-indigo-300"
            placeholder="Set value"
            value={config.value ?? ""}
            onChange={(e) => update({ value: e.target.value })}
          />
          <button className="absolute right-2 top-2 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-indigo-500">
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-none stroke-current stroke-2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M9 9h6M9 12h6M9 15h4" />
            </svg>
          </button>
        </div>

        <div className="space-y-1.5">
          {["true", "false"].map((v) => (
            <button
              key={v}
              onClick={() => update({ value: v })}
              className={`w-full rounded-lg px-3 py-2 text-sm text-left transition-colors border ${
                config.value === v
                  ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                  : "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
    </CardShell>
  );
};